import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trophy, Play, ExternalLink, Copy, CheckCircle, X, Mail, Users, UserPlus,
  MapPin, Ruler, Star, Twitter, ArrowRight, Calendar, GraduationCap, User,
  Dumbbell, Search, Zap, Film, BarChart3, Building2, Instagram, Phone, Clock, Image as ImageIcon, LayoutDashboard, Eye
} from 'lucide-react';
import Hls from 'hls.js';
import './profileThemes.css';
import { API } from '../lib/constants';

const GREEN = '#0F5132';

// ── Video Player ──
const VideoPlayer = ({ src, poster }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    if (src.includes('.m3u8') && Hls.isSupported()) {
      const h = new Hls({ startLevel: -1, maxBufferLength: 10 });
      hlsRef.current = h; h.loadSource(src); h.attachMedia(v);
    } else { v.src = src; }
    return () => { if (hlsRef.current) hlsRef.current.destroy(); };
  }, [src]);
  return <video ref={videoRef} controls playsInline poster={poster} className="w-full h-full object-cover" />;
};

// ── Tweet Embed (offer announcement) ──
const tweetUrlFor = (o) => o?.tweet_url || (o?.sourceUrl && /\/status\//.test(o.sourceUrl) ? o.sourceUrl : null);

const TweetEmbed = ({ url }) => {
  const [html, setHtml] = useState('');
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/twitter/oembed`, { params: { url } })
      .then(r => { if (!cancelled) { r.data?.html ? setHtml(r.data.html) : setFailed(true); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [url]);
  useEffect(() => {
    if (!html) return;
    const render = () => { if (window.twttr?.widgets && ref.current) window.twttr.widgets.load(ref.current); };
    if (window.twttr?.widgets) { render(); return; }
    let s = document.getElementById('twitter-wjs');
    if (!s) {
      s = document.createElement('script');
      s.id = 'twitter-wjs'; s.src = 'https://platform.twitter.com/widgets.js'; s.async = true;
      document.body.appendChild(s);
    }
    s.addEventListener('load', render);
    const t = setTimeout(render, 1500);
    return () => { s.removeEventListener('load', render); clearTimeout(t); };
  }, [html]);
  if (failed) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid="offer-tweet-link"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1DA1F2] hover:underline mt-3">
        <Twitter className="h-4 w-4" /> View offer announcement on X <ExternalLink className="h-3.5 w-3.5" />
      </a>
    );
  }
  if (!html) return <div className="text-xs text-slate-400 mt-3" data-testid="offer-tweet-loading">Loading announcement…</div>;
  return <div ref={ref} className="offer-tweet-embed mt-3 [&_.twitter-tweet]:!my-0" data-testid="offer-tweet-embed" dangerouslySetInnerHTML={{ __html: html }} />;
};

// ── Contact Modal ──
const ContactModal = ({ username, playerName, onClose }) => {
  const [form, setForm] = useState({ sender_name: '', sender_email: '', sender_role: '', sender_school: '', message: '' });
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!form.sender_name || !form.sender_email || !form.message) { toast.error('Fill in name, email & message'); return; }
    setSending(true);
    try { await axios.post(`${API}/api/contact/${username}`, form); toast.success('Message sent!'); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };
  const cls = 'w-full px-3 py-2.5 rounded-xl text-sm border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F5132]/20';
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 bg-white border border-slate-200 rounded-3xl p-6 shadow-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="contact-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400"><X className="h-5 w-5" /></button>
        <h3 className="font-extrabold text-lg mb-1 text-slate-900">Message {playerName?.split(' ')[0]}</h3>
        <p className="text-slate-500 text-xs mb-4">Verified coaches & recruiters only. Keep it professional.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input value={form.sender_name} onChange={e => setForm(p => ({ ...p, sender_name: e.target.value }))} placeholder="Your Name *" className={cls} data-testid="contact-name" />
            <input value={form.sender_email} onChange={e => setForm(p => ({ ...p, sender_email: e.target.value }))} placeholder="Email *" type="email" className={cls} data-testid="contact-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={form.sender_role} onChange={e => setForm(p => ({ ...p, sender_role: e.target.value }))} placeholder="Role (Coach, etc)" className={cls} />
            <input value={form.sender_school} onChange={e => setForm(p => ({ ...p, sender_school: e.target.value }))} placeholder="School/Org" className={cls} />
          </div>
          <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Your message... *" rows={3} className={`${cls} resize-none`} data-testid="contact-message" />
          <button onClick={send} disabled={sending} className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold py-3 rounded-full text-sm disabled:opacity-50" data-testid="contact-send">{sending ? 'Sending...' : 'Send Message'}</button>
        </div>
      </div>
    </div>
  );
};

// ── Reusable info card ──
const InfoCard = ({ icon, title, children, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200/80 p-5 sm:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4 text-[#0F5132]">{icon}<h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</h3></div>
    {children}
  </div>
);
const Row = ({ label, value }) => (value === undefined || value === null || value === '' ? null : (
  <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-slate-500 text-sm">{label}</span><span className="text-slate-900 font-bold text-sm">{value}</span>
  </div>
));
const Empty = ({ icon, text }) => (
  <div className="bg-slate-50 rounded-2xl py-16 flex flex-col items-center justify-center text-center">
    <div className="text-slate-300 mb-3">{icon}</div><p className="text-slate-400 font-medium">{text}</p>
  </div>
);

const TABS = [
  { id: 'about', label: 'About', icon: <User className="h-4 w-4" /> },
  { id: 'film', label: 'Film', icon: <Film className="h-4 w-4" /> },
  { id: 'schools', label: 'Schools', icon: <Building2 className="h-4 w-4" /> },
  { id: 'stats', label: 'Stats', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'events', label: 'Events', icon: <Calendar className="h-4 w-4" /> },
  { id: 'photos', label: 'Photos', icon: <ImageIcon className="h-4 w-4" /> },
  { id: 'network', label: 'Network', icon: <Users className="h-4 w-4" /> },
];

const PublicProfile = ({ preview = false }) => {
  const { username } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState([]);
  const [camps, setCamps] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [viewCount, setViewCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeAd, setActiveAd] = useState(null);
  const impressionFired = useRef(false);
  const [tab, setTab] = useState('about');
  const [schoolSub, setSchoolSub] = useState('offers');
  const [photos, setPhotos] = useState([]);
  const [underReview, setUnderReview] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  // Detect a signed-in viewer (valid session cookie) to offer a quick Dashboard shortcut
  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(() => setSignedIn(true))
      .catch(() => setSignedIn(false));
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const viewer = localStorage.getItem('userId');
        const endpoint = preview ? `${API}/api/profile/${username}/preview` : `${API}/api/profile/${username}`;
        const res = await axios.get(endpoint, { params: (!preview && viewer) ? { viewer_user_id: viewer } : {} });
        if (!preview && res.data.under_review) { setProfile(res.data); setUnderReview(true); setLoading(false); return; }
        setProfile(res.data);
        axios.get(`${API}/api/ads/active/${username}`).then(r => setActiveAd(r.data.ad)).catch(() => {});
        axios.get(`${API}/api/profile/${username}/photos`).then(r => setPhotos(r.data.photos || [])).catch(() => {});
        const uid = res.data.profile?.user_id;
        if (uid) {
          const [oR, sR, cR, fR, gR, aR] = await Promise.all([
            axios.get(`${API}/api/dashboard/${uid}/offers`).catch(() => ({ data: { offers: [] } })),
            axios.get(`${API}/api/dashboard/${uid}/comprehensive-stats`).catch(() => ({ data: { stats: [] } })),
            axios.get(`${API}/api/dashboard/${uid}/camps`).catch(() => ({ data: { camps: [] } })),
            axios.get(`${API}/api/profile/${uid}/followers`).catch(() => ({ data: { followers: [] } })),
            axios.get(`${API}/api/profile/${uid}/following`).catch(() => ({ data: { following: [] } })),
            axios.get(`${API}/api/analytics/${uid}`).catch(() => ({ data: { total_views: 0 } })),
          ]);
          setOffers(oR.data.offers || []);
          setStats(sR.data.stats || []);
          setCamps(cR.data.camps || []);
          setFollowers(fR.data.followers || []);
          setFollowing(gR.data.following || []);
          setViewCount(aR.data.total_views || 0);
          const cuid = localStorage.getItem('userId');
          if (cuid && cuid !== uid) {
            const sRes = await axios.get(`${API}/api/follow-status/${cuid}/${uid}`).catch(() => ({ data: {} }));
            setIsFollowing(sRes.data.is_following || false);
          }
        }
        if (!preview) {
          try { await axios.post(`${API}/api/profile/${username}/view`, null, { params: { viewer_user_id: localStorage.getItem('userId') } }); } catch {}
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [username, preview]);

  useEffect(() => {
    if (activeAd && activeAd.id && !impressionFired.current) {
      impressionFired.current = true;
      axios.post(`${API}/api/ads/${activeAd.id}/impression`, null, { params: { username } }).catch(() => {});
    }
  }, [activeAd, username]);

  const copyLink = () => { navigator.clipboard.writeText(`${window.location.origin}/api/share/${username}`); setCopied(true); toast.success('Share link copied!'); setTimeout(() => setCopied(false), 2000); };
  const ensureHttps = (u) => u && !u.startsWith('http') ? `https://${u}` : u || '';
  const resolveAdImg = (u) => (u && u.startsWith('/api') ? `${API}${u}` : u);

  const toggleFollow = async () => {
    const cuid = localStorage.getItem('userId');
    if (!cuid) { toast.error('Sign in to follow'); return; }
    try {
      if (isFollowing) { await axios.delete(`${API}/api/follow/${profile.profile.user_id}?user_id=${cuid}`); setIsFollowing(false); }
      else { await axios.post(`${API}/api/follow?user_id=${cuid}`, { following_id: profile.profile.user_id }); setIsFollowing(true); }
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#0F5132] border-t-transparent rounded-full animate-spin" /></div>;
  if (!profile) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-900 px-4 text-center">
      <div><h1 className="text-2xl font-extrabold mb-2">Profile Not Found</h1><p className="text-slate-400 mb-6">@{username} doesn't exist yet.</p>
      <button onClick={() => navigate('/create-profile')} className="bg-[#0F5132] text-white font-bold px-8 py-3 rounded-full">Create Your Profile</button></div>
    </div>
  );

  if (underReview) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-900 px-4 text-center" data-testid="profile-under-review">
      <div className="max-w-sm">
        <div className="w-14 h-14 rounded-full bg-[#DCFCE7] flex items-center justify-center mx-auto mb-4"><Clock className="h-7 w-7 text-[#0F5132]" /></div>
        <h1 className="text-2xl font-extrabold mb-2">Profile under review</h1>
        <p className="text-slate-500 mb-6">@{username} is being reviewed by our team and will be live soon. Check back shortly.</p>
        <button onClick={() => navigate('/directory')} className="bg-[#0F5132] text-white font-bold px-8 py-3 rounded-full">Discover other players</button>
      </div>
    </div>
  );

  const p = profile.profile;
  const rosters = profile.rosters || [];
  const m = profile.measurables || {};
  const coach = profile.coach;
  const highlights = profile.highlights || [];
  const realOffers = offers.filter(o => o.status === 'offer' || o.status === 'committed');
  const interestedOffers = offers.filter(o => o.status === 'interested');
  const isOwnProfile = localStorage.getItem('userId') === p.user_id;
  const classShort = p.class_year ? `'${String(p.class_year).slice(-2)}` : '';
  const showLoc = p.show_location !== false && p.city;

  // ── Ad Slot ──
  const AdSlot = () => {
    if (activeAd) return (
      <a href={`${API}/api/ads/${activeAd.id}/click?username=${encodeURIComponent(username)}`} target="_blank" rel="noopener noreferrer" data-testid="profile-ad-sold"
        className="relative block rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm group">
        <div className="aspect-[5/1] w-full overflow-hidden"><img src={resolveAdImg(activeAd.image_url)} alt={activeAd.advertiser_name || 'Ad'} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" /></div>
        <span className="absolute top-2 right-2 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/90 text-slate-500">Ad</span>
        {activeAd.advertiser_name && <div className="absolute bottom-0 left-0 right-0 px-3 py-1.5 text-xs font-semibold flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent text-white"><span className="truncate">{activeAd.advertiser_name}</span><span className="text-[#4ade80] flex items-center gap-1">Visit <ArrowRight className="h-3 w-3" /></span></div>}
      </a>
    );
    // No active sold ad → render nothing (no house placeholder).
    return null;
  };

  const HeaderBtns = () => (
    <div className="flex items-center gap-2">
      {signedIn && (
        <button onClick={() => navigate('/dashboard')} data-testid="header-dashboard" title="Dashboard" className="flex items-center justify-center h-11 w-11 sm:w-auto sm:px-4 gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold"><LayoutDashboard className="h-5 w-5 sm:h-4 sm:w-4" /><span className="hidden sm:inline">Dashboard</span></button>
      )}
      <button onClick={() => navigate('/directory')} data-testid="header-discover" className="hidden sm:flex items-center gap-1.5 h-11 px-4 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold"><Search className="h-4 w-4" /> Discover</button>
      <button onClick={copyLink} data-testid="header-copy" title="Copy link" className="flex items-center justify-center h-11 w-11 sm:w-auto sm:px-4 gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold">{copied ? <CheckCircle className="h-5 w-5 sm:h-4 sm:w-4 text-[#0F5132]" /> : <Copy className="h-5 w-5 sm:h-4 sm:w-4" />}<span className="hidden sm:inline">{copied ? 'Copied' : 'Copy Link'}</span></button>
      <button onClick={() => setShowContact(true)} data-testid="header-contact" className="flex items-center gap-2 h-11 px-5 sm:px-6 rounded-full bg-[#0F5132] hover:bg-[#0B3B24] text-white text-base font-black shadow-sm"><Mail className="h-5 w-5" /> Contact</button>
    </div>
  );

  return (
    <div className={`min-h-screen bg-slate-50 text-slate-900 oz-theme-${p.theme || 'dark'}`} data-testid="profile-root" data-theme={p.theme || 'dark'}>
      {/* Sticky mini header */}
      <div className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full overflow-hidden bg-slate-200 flex-shrink-0">
              {p.profile_picture_url ? <img src={p.profile_picture_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#0F5132] flex items-center justify-center text-white text-sm font-black">{p.name?.charAt(0)}</div>}
            </div>
            <div className="min-w-0"><p className="font-extrabold text-sm text-slate-900 truncate leading-tight">{p.name}</p><p className="text-slate-400 text-xs truncate">{p.positions?.[0]} · {classShort}</p></div>
          </div>
          <HeaderBtns />
        </div>
      </div>

      {/* Preview mode banner — shown to prospective (unclaimed) players */}
      {preview && (
        <div className="w-full bg-gradient-to-r from-[#0F5132] to-[#16A34A] text-white text-center py-2.5 px-4 flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold" data-testid="preview-banner">
          <Eye className="h-4 w-4 text-[#bbf7d0]" /> This is a preview of your Offersz page — it's not live yet.
        </div>
      )}

      {/* Always-visible Create CTA strip (logged-out visitors only, non-preview) */}
      {!preview && !isOwnProfile && !signedIn && (
        <button onClick={() => navigate('/create-profile')} data-testid="create-profile-strip"
          className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white text-xs sm:text-sm font-semibold py-2 px-4 flex items-center justify-center gap-2 transition-colors">
          <Zap className="h-3.5 w-3.5 text-[#4ade80]" /> Create your own free Offersz profile <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}

      <div className={`max-w-5xl mx-auto px-4 sm:px-6 pt-8 ${preview ? 'pb-44 md:pb-28' : 'pb-28 md:pb-8'}`}>
        {/* ── HERO ── */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 items-start mb-8">
          <div className="w-28 h-28 sm:w-40 sm:h-40 rounded-full overflow-hidden ring-4 ring-white shadow-[0_8px_30px_rgb(0,0,0,0.08)] flex-shrink-0">
            {p.profile_picture_url ? <img src={p.profile_picture_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#0F5132] flex items-center justify-center text-5xl font-black text-white">{p.name?.charAt(0)}</div>}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mb-3">{p.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {p.positions?.map((pos, i) => <span key={i} className="bg-[#DCFCE7] text-[#0F5132] text-sm font-bold px-3 py-1 rounded-full">{pos}</span>)}
              {p.jersey_number && <span className="bg-slate-100 text-slate-500 text-sm font-bold px-3 py-1 rounded-full">#{p.jersey_number}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-slate-500 text-sm sm:text-base">
              {p.school && <span className="flex items-center gap-1.5"><GraduationCap className="h-4 w-4" /> {p.school}</span>}
              {p.class_year && <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> Class of {p.class_year}</span>}
              {showLoc && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {p.city}{p.state ? `, ${p.state}` : ''}</span>}
            </div>
            {rosters.filter(r => !r.is_primary && r.slug).length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="also-plays-for">
                <span className="text-slate-400 text-sm font-semibold">Also plays for</span>
                {rosters.filter(r => !r.is_primary && r.slug).map(r => (
                  <button key={r.id} onClick={() => navigate(`/team/${r.slug}`)} data-testid={`roster-chip-${r.slug}`}
                    className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-3 py-1 rounded-full text-xs transition-colors">
                    <Users className="h-3.5 w-3.5" /> {r.name}
                  </button>
                ))}
              </div>
            )}
            {/* social + follow */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {p.hudl_url && <a href={ensureHttps(p.hudl_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50 text-orange-600 text-xs font-bold hover:bg-orange-100" data-testid="social-hudl"><ExternalLink className="h-3.5 w-3.5" /> HUDL</a>}
              {p.x_url && <a href={ensureHttps(p.x_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200" data-testid="social-x"><Twitter className="h-3.5 w-3.5" /> X</a>}
              {p.instagram_url && <a href={ensureHttps(p.instagram_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pink-50 text-pink-600 text-xs font-bold hover:bg-pink-100" data-testid="social-ig"><Instagram className="h-3.5 w-3.5" /> Instagram</a>}
              {(p.custom_links || []).filter(l => l && l.url).map((l, i) => <a key={i} href={ensureHttps(l.url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#DCFCE7] text-[#0F5132] text-xs font-bold hover:bg-[#bbf7d0]" data-testid={`social-custom-${i}`}><ExternalLink className="h-3.5 w-3.5" /> {l.label || 'Link'}</a>)}
              {!isOwnProfile && <button onClick={toggleFollow} data-testid="follow-btn" className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold ${isFollowing ? 'bg-slate-100 text-slate-700' : 'bg-[#0F5132] text-white hover:bg-[#0B3B24]'}`}><UserPlus className="h-3.5 w-3.5" /> {isFollowing ? 'Following' : 'Follow'}</button>}
              {viewCount > 0 && <span className="text-slate-400 text-xs ml-1">{viewCount} profile views</span>}
            </div>
          </div>
        </div>

        {/* Ad slot — only when a sold ad is active */}
        {activeAd && <div className="mb-6"><AdSlot /></div>}

        {/* ── TAB BAR (desktop / tablet) ── */}
        <div className="hidden md:flex bg-slate-100 p-1.5 rounded-full overflow-x-auto scrollbar-hide gap-1 mb-6 border border-slate-200/60" data-testid="profile-tabs">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`profile-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB CONTENT ── */}
        <div data-testid={`tab-content-${tab}`}>
          {tab === 'about' && (
            <div>
              {p.tagline && <div className="border-l-4 border-[#0F5132] bg-[#DCFCE7]/40 p-4 rounded-r-2xl text-slate-800 italic font-semibold text-lg mb-4">"{p.tagline}"</div>}
              {p.about_me && <p className="text-slate-600 leading-relaxed mb-6">{p.about_me}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCard icon={<Ruler className="h-4 w-4" />} title="Measurables" testid="card-measurables">
                  <Row label="Height" value={m.height || p.height} />
                  <Row label="Weight" value={(m.weight || p.weight) ? `${m.weight || p.weight} lbs` : null} />
                  <Row label="40-Yard Dash" value={m.forty_yard_dash ? `${m.forty_yard_dash}s` : null} />
                  <Row label="Bench Press" value={m.bench_press ? `${m.bench_press} lbs` : null} />
                  <Row label="Squat" value={m.squat ? `${m.squat} lbs` : null} />
                  <Row label="Vertical Jump" value={m.vertical_jump ? `${m.vertical_jump}"` : null} />
                  <Row label="Broad Jump" value={m.broad_jump} />
                  <Row label="Shuttle" value={m.shuttle ? `${m.shuttle}s` : null} />
                  {!m.height && !m.weight && !m.forty_yard_dash && !p.height && <p className="text-slate-400 text-sm">No measurables added yet.</p>}
                </InfoCard>
                <InfoCard icon={<User className="h-4 w-4" />} title="Info" testid="card-info">
                  <Row label="Position(s)" value={p.positions?.join(', ')} />
                  <Row label="Jersey" value={p.jersey_number ? `#${p.jersey_number}` : null} />
                  <Row label="School" value={p.school} />
                  <Row label="Class Year" value={p.class_year} />
                  {showLoc && <Row label="Hometown" value={`${p.city}${p.state ? `, ${p.state}` : ''}`} />}
                </InfoCard>
                <InfoCard icon={<GraduationCap className="h-4 w-4" />} title="Academics" testid="card-academics">
                  <Row label="GPA" value={p.gpa} />
                  <Row label="SAT" value={p.sat_score} />
                  {!p.gpa && !p.sat_score && <p className="text-slate-400 text-sm">No academics added yet.</p>}
                </InfoCard>
                <InfoCard icon={<Dumbbell className="h-4 w-4" />} title="Head Coach" testid="card-coach">
                  {coach && (coach.name || coach.email) ? (<>
                    <Row label="Coach" value={coach.name} />
                    <Row label="Phone" value={coach.phone} />
                    <Row label="Email" value={coach.email} />
                  </>) : <p className="text-slate-400 text-sm">No coach info added.</p>}
                </InfoCard>
              </div>
            </div>
          )}

          {tab === 'film' && (
            highlights.length === 0 ? <Empty icon={<Film className="h-10 w-10" />} text="No highlights added yet" /> : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {highlights.map((vid, i) => {
                  const src = vid.og_video_secure_url || vid.og_video_url;
                  return (
                    <div key={i} className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid={`film-${i}`}>
                      <div className="aspect-video bg-slate-900 relative">
                        {src ? <VideoPlayer src={src} poster={vid.og_image} /> : vid.og_image ? (
                          <a href={ensureHttps(vid.hudl_video_url)} target="_blank" rel="noopener noreferrer" className="block w-full h-full group">
                            <img src={vid.og_image} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20"><div className="w-14 h-14 bg-[#0F5132] rounded-full flex items-center justify-center"><Play className="h-6 w-6 text-white ml-0.5" /></div></div>
                          </a>
                        ) : <div className="w-full h-full flex items-center justify-center"><Play className="h-10 w-10 text-white/20" /></div>}
                        {vid.video_duration && <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{vid.video_duration}</span>}
                      </div>
                      <div className="p-3"><p className="font-bold text-sm text-slate-900 truncate">{vid.video_title || `Highlight ${i + 1}`}</p>{vid.video_description && <p className="text-slate-400 text-xs truncate">{vid.video_description}</p>}</div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {tab === 'stats' && (
            stats.length === 0 ? <Empty icon={<BarChart3 className="h-10 w-10" />} text="No stats added yet" /> : (
              <div className="space-y-4">
                {stats.map((s, si) => {
                  const entries = [...(s.offensive_stats ? Object.entries(s.offensive_stats) : []), ...(s.defensive_stats ? Object.entries(s.defensive_stats) : [])].filter(([_, v]) => v && v !== 0);
                  return (
                    <div key={si} className="bg-white rounded-2xl border border-slate-200/80 p-5 sm:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid={`stats-season-${si}`}>
                      <div className="flex items-center gap-2 mb-4"><Star className="h-5 w-5 text-[#0F5132]" /><h3 className="text-lg font-extrabold">{s.season}</h3>{s.games_played && <span className="bg-slate-100 text-slate-500 text-xs font-bold px-2 py-0.5 rounded-full ml-auto">{s.games_played} GP</span>}</div>
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                        {entries.map(([k, v]) => <div key={k} className="bg-slate-50 rounded-xl p-3 text-center"><p className="text-xl font-extrabold text-slate-900">{v}</p><p className="text-slate-400 text-[9px] uppercase tracking-wider">{k.replace(/_/g, ' ')}</p></div>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {tab === 'schools' && (
            <div>
              <div className="bg-slate-100 p-1.5 rounded-full flex gap-1 mb-5 max-w-sm">
                <button onClick={() => setSchoolSub('offers')} data-testid="schools-sub-offers" className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${schoolSub === 'offers' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}><Trophy className="h-4 w-4" /> Offers {realOffers.length > 0 && <span className="text-xs">{realOffers.length}</span>}</button>
                <button onClick={() => setSchoolSub('interested')} data-testid="schools-sub-interested" className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${schoolSub === 'interested' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}><Star className="h-4 w-4" /> Interested {interestedOffers.length > 0 && <span className="text-xs">{interestedOffers.length}</span>}</button>
              </div>
              {(() => {
                const list = schoolSub === 'offers' ? realOffers : interestedOffers;
                if (list.length === 0) return <Empty icon={<Trophy className="h-10 w-10" />} text={schoolSub === 'offers' ? 'No offers yet' : 'No interested schools yet'} />;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {list.map((offer, i) => {
                      const tUrl = tweetUrlFor(offer);
                      return (
                      <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid={`school-${i}`}>
                        <div className="flex items-center gap-4">
                          {offer.college?.logoUrl ? <img src={offer.college.logoUrl} alt="" className="w-12 h-12 rounded-xl object-contain bg-white p-1 border border-slate-100" /> : <div className="w-12 h-12 rounded-xl bg-slate-100" />}
                          <div className="flex-1 min-w-0"><p className="font-bold text-slate-900">{offer.college?.name || 'University'}</p><p className="text-slate-400 text-xs">{offer.college?.division}</p></div>
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${offer.status === 'committed' ? 'bg-amber-100 text-amber-700' : offer.status === 'offer' ? 'bg-[#DCFCE7] text-[#0F5132]' : 'bg-blue-50 text-blue-600'}`}>{offer.status}</span>
                        </div>
                        {tUrl && <TweetEmbed url={tUrl} />}
                      </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {tab === 'events' && (
            camps.length === 0 ? <Empty icon={<Calendar className="h-10 w-10" />} text="No events yet" /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {camps.map((c, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-testid={`event-${i}`}>
                    <div className="flex items-center gap-2 mb-1"><Calendar className="h-4 w-4 text-[#0F5132]" /><p className="font-bold text-slate-900">{c.camp_name || c.name || 'Event'}</p></div>
                    {c.location && <p className="text-slate-500 text-sm flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.location}</p>}
                    {(c.date || c.camp_date) && <p className="text-slate-400 text-xs mt-1">{c.date || c.camp_date}</p>}
                  </div>
                ))}
              </div>
            )
          )}

          {tab === 'photos' && (
            photos.length === 0 ? <Empty icon={<ImageIcon className="h-10 w-10" />} text="No photos yet" /> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photos-grid">
                {photos.map((ph, i) => (
                  <a key={ph.id} href={`${API}${ph.url}`} target="_blank" rel="noopener noreferrer" className="block rounded-2xl overflow-hidden aspect-square bg-slate-100 border border-slate-200/80" data-testid={`photo-${i}`}>
                    <img src={`${API}${ph.url}`} alt={ph.caption || ''} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                  </a>
                ))}
              </div>
            )
          )}

          {tab === 'network' && (
            <div className="space-y-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Followers ({followers.length})</p>
                {followers.length === 0 ? <Empty icon={<Users className="h-10 w-10" />} text="No followers yet" /> : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {followers.map((f, i) => (
                      <button key={i} onClick={() => f.username && navigate(`/profile/${f.username}`)} className="bg-white rounded-2xl border border-slate-200/80 p-4 flex flex-col items-center text-center hover:shadow-md transition-all" data-testid={`follower-${i}`}>
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 mb-2">{f.profile_picture_url ? <img src={f.profile_picture_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#0F5132] flex items-center justify-center text-white font-black">{(f.name || '?').charAt(0)}</div>}</div>
                        <p className="font-bold text-sm text-slate-900 truncate w-full">{f.name || f.username}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Following ({following.length})</p>
                {following.length === 0 ? <Empty icon={<UserPlus className="h-10 w-10" />} text="Not following anyone yet" /> : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {following.map((f, i) => (
                      <button key={i} onClick={() => f.username && navigate(`/profile/${f.username}`)} className="bg-white rounded-2xl border border-slate-200/80 p-4 flex flex-col items-center text-center hover:shadow-md transition-all" data-testid={`following-${i}`}>
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 mb-2">{f.profile_picture_url ? <img src={f.profile_picture_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#0F5132] flex items-center justify-center text-white font-black">{(f.name || '?').charAt(0)}</div>}</div>
                        <p className="font-bold text-sm text-slate-900 truncate w-full">{f.name || f.username}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Viral banner (compact) */}
        {preview ? (
          <div className="mt-8 bg-[#DCFCE7] border border-[#86EFAC] rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[#14532D]"><Trophy className="h-4 w-4" /><p className="font-bold text-sm">This is your Offersz page, built for you. Claim it to make it live and start editing.</p></div>
            <button onClick={() => navigate(`/claim/${username}`)} data-testid="preview-claim-inline-cta" className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold text-sm px-6 py-2.5 rounded-full whitespace-nowrap flex items-center gap-1.5">Claim my profile <ArrowRight className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="mt-8 bg-[#DCFCE7] border border-[#86EFAC] rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[#14532D]"><Zap className="h-4 w-4" /><p className="font-bold text-sm">Created with Offersz — the recruiting link for high school football players.</p></div>
            <button onClick={() => navigate('/create-profile')} data-testid="viral-banner-cta" className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold text-sm px-6 py-2.5 rounded-full whitespace-nowrap flex items-center gap-1.5">Claim your profile <ArrowRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      {/* Preview: fixed Claim CTA bar (sits above mobile tab bar) */}
      {preview && (
        <div className="fixed inset-x-0 bottom-[4.25rem] md:bottom-0 z-50 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] md:pb-3" data-testid="preview-claim-bar">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
            <p className="text-sm font-bold text-slate-800 hidden sm:block">Like what you see? Make it yours.</p>
            <button onClick={() => navigate(`/claim/${username}`)} data-testid="preview-claim-btn"
              className="flex-1 sm:flex-none bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold px-6 py-3 rounded-full flex items-center justify-center gap-2">
              <Trophy className="h-4 w-4 text-[#4ade80]" /> Claim my profile <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── MOBILE BOTTOM TAB BAR ── */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgb(0,0,0,0.06)] pb-[env(safe-area-inset-bottom)]" data-testid="profile-tabs-mobile">
        <div className="flex items-stretch justify-around">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`profile-tab-mobile-${t.id}`}
              className={`flex-1 flex flex-col items-center justify-center gap-1 py-2.5 transition-colors ${tab === t.id ? 'text-[#0F5132]' : 'text-slate-400'}`}>
              {t.icon}
              <span className="text-[11px] font-bold leading-none">{t.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {showContact && <ContactModal username={username} playerName={p.name} onClose={() => setShowContact(false)} />}
    </div>
  );
};

export default PublicProfile;
